import { popoverPlacementClass, type PopoverPlacement } from "../workspace/popover-placement";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useNavigate } from "react-router";
import type { MessageKey } from "@wato/i18n";
import { useI18n } from "@wato/i18n/react";
import { Button, Icon, IconButton } from "@wato/ui";
import { useApi } from "../../api/context";
import type { NotificationView } from "../../api/notification-types";
import { useRealtimeFallbackPolling } from "../../lib/useRealtimeFallbackPolling";
import { useModuleFlags } from "../workspace/useModules";
import { visibleNotifications } from "./notification-module";

const POLL_MS = 30_000;
/** Same event name as `HEADER_POPOVER_EVENT` in `workspace/HeaderMenus` (one popover at a time). */
const HEADER_POPOVER_EVENT = "wato:header-popover-open";

/** Only same-app relative links are followed (no open redirect through a stored link). */
export function safeLink(link: string | null): string | null {
  if (!link || !link.startsWith("/") || link.startsWith("//") || link.includes("\\")) return null;
  return link;
}

/**
 * WP-422 header bell: unread badge + dropdown list. Polls the unread count (no WS event yet);
 * opening the dropdown loads the latest notifications. Clicking one marks it read and follows
 * its link; "Mark all as read" clears the badge.
 */
export function NotificationBell({
  workspaceId,
  placement = "end-down",
}: {
  workspaceId: string;
  placement?: PopoverPlacement;
}) {
  const { t } = useI18n();
  const api = useApi();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState<NotificationView[]>([]);
  const [tab, setTab] = useState<"all" | NotificationView["category"]>("all");
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const popId = useId();
  const { isOn, flags } = useModuleFlags();
  const someModuleOff = Object.values(flags).some((on) => on === false);

  // Q4-XCT-03: Esc, an outside click, or another header popover opening closes the bell.
  useEffect(() => {
    if (!open) return;
    window.dispatchEvent(new CustomEvent(HEADER_POPOVER_EVENT, { detail: popId }));
    const onOther = (e: Event) => {
      if ((e as CustomEvent<string>).detail !== popId) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        button.current?.focus();
      }
    };
    const onDown = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener(HEADER_POPOVER_EVENT, onOther);
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      window.removeEventListener(HEADER_POPOVER_EVENT, onOther);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open, popId]);

  // OWN-333: with a module off, the server count still holds its notifications, so count the visible unread list.
  const refreshCount = useCallback(() => {
    const counted = someModuleOff
      ? api
          .listNotifications(workspaceId, true)
          .then((list) => visibleNotifications(list, isOn).length)
      : api.getUnreadNotificationCount(workspaceId);
    counted.then(
      (n) => setCount(n),
      () => undefined,
    );
  }, [api, workspaceId, someModuleOff, isOn]);

  useEffect(() => {
    refreshCount();
  }, [refreshCount]);
  useRealtimeFallbackPolling(refreshCount, POLL_MS);

  const load = useCallback(
    (category: "all" | NotificationView["category"]) => {
      api.listNotifications(workspaceId, false, category === "all" ? undefined : category).then(
        (list) => setItems(visibleNotifications(list, isOn)),
        () => undefined,
      );
    },
    [api, workspaceId, isOn],
  );

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      load(tab);
      refreshCount();
    }
  };

  const selectTab = (next: "all" | NotificationView["category"]) => {
    setTab(next);
    load(next);
  };

  const openItem = (item: NotificationView) => {
    if (!item.readAt) {
      setItems((prev) =>
        prev.map((n) => (n.id === item.id ? { ...n, readAt: new Date().toISOString() } : n)),
      );
      setCount((c) => Math.max(0, c - 1));
      api.markNotificationRead(workspaceId, item.id).catch(refreshCount);
    }
    const link = safeLink(item.link);
    if (link) {
      setOpen(false);
      navigate(link);
    }
  };

  const markAll = () => {
    setItems((prev) =>
      prev.map((n) => (n.readAt ? n : { ...n, readAt: new Date().toISOString() })),
    );
    setCount(0);
    api.markAllNotificationsRead(workspaceId).catch(refreshCount);
  };

  return (
    <div ref={root} className="relative">
      <Button
        ref={button}
        variant="ghost"
        size="sm"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={
          count > 0
            ? `${t("notifications.bell.label")}: ${t("notifications.bell.unread", { count })}`
            : t("notifications.bell.label")
        }
        onClick={toggle}
        className="w-8 px-0!"
      >
        <span aria-hidden="true">{"\u{1F514}"}</span>
        {count > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-1 -end-1 min-w-4 rounded-full bg-danger-bg px-1 text-center text-xs text-danger-bg-fg"
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </Button>
      {open && (
        <div
          role="region"
          aria-label={t("notifications.bell.label")}
          className={`absolute z-20 w-80 max-w-[90vw] rounded-lg border border-line bg-surface p-2 shadow-lg ${popoverPlacementClass(placement)}`}
        >
          <div className="flex items-center justify-between gap-2 px-2 pb-2">
            <p className="text-sm font-semibold text-fg">{t("notifications.bell.label")}</p>
            {count > 0 && (
              <IconButton
                variant="ghost"
                label={t("notifications.bell.markAllRead")}
                icon={<Icon name="check" />}
                onClick={markAll}
              />
            )}
          </div>
          <div role="tablist" className="flex flex-wrap gap-1 px-2 pb-2">
            {(["all", "inbox", "billing", "compliance", "system"] as const).map((c) => (
              <Button
                key={c}
                role="tab"
                size="sm"
                variant={tab === c ? "primary" : "secondary"}
                aria-selected={tab === c}
                onClick={() => selectTab(c)}
              >
                {t(`notifications.bell.tabs.${c}` as MessageKey)}
              </Button>
            ))}
          </div>
          {items.length === 0 ? (
            <p className="px-2 py-4 text-sm text-fg-muted">{t("notifications.bell.empty")}</p>
          ) : (
            <ul className="flex max-h-96 flex-col gap-1 overflow-y-auto">
              {items.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => openItem(item)}
                    className={`w-full rounded-md px-2 py-2 text-start hover:bg-surface-muted ${
                      item.readAt ? "text-fg-muted" : "font-semibold text-fg"
                    }`}
                  >
                    <span className="block text-sm">
                      {t(item.titleKey as MessageKey, item.params)}
                    </span>
                    {item.bodyKey && (
                      <span className="block text-xs font-normal text-fg-muted">
                        {t(item.bodyKey as MessageKey, item.params)}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <Button
            variant="ghost"
            size="sm"
            fullWidth
            className="mt-1"
            onClick={() => {
              setOpen(false);
              navigate(`/w/${workspaceId}/notifications`);
            }}
          >
            {t("notifications.bell.seeAll")}
          </Button>
        </div>
      )}
    </div>
  );
}
